import { AnnotationEditorType } from '@ethsign/pdf-editor';
import { shortenWalletAddress } from '@ethsign/utils-web';
import xml2js from 'xml2js';

import { beraLogoImg, logoImg, singpassImg, uaeImg } from '@/constants/editor';
import { ICurrentUser } from '@/types';

import { getNanoidEn } from './common';
import { generateImg, getTextWidth } from './dom';

function getLines(text: string, maxWidth: number, font: string) {
  const words = text.split('');
  const lines = [];
  let currentLine = words[0];

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = getTextWidth(currentLine + word, font);
    if (width < maxWidth) {
      currentLine += word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  lines.push(currentLine);
  return lines;
}
function calculateCanvasSize({
  canvas,
  eUserName,
  eUserId,
  font,
  maxWidth,
  font500,
  ens
}: {
  canvas: HTMLCanvasElement;
  eUserName?: string;
  eUserId?: string;
  font: string;
  maxWidth: number;
  font500: string;
  ens?: string;
}) {
  let eText = '';
  let isLongText = false;
  let ensLines: string[] = [];
  if (eUserName && eUserId) {
    const formatedEUserName = `${eUserName.split(' ').join('.')} (*${eUserId.slice(-4)})`;
    const formatedEUserNameWidth = getTextWidth(formatedEUserName, font);
    eText = formatedEUserName;
    isLongText = formatedEUserNameWidth > maxWidth;
    const width = formatedEUserNameWidth + (isLongText ? 80 : 185);
    canvas.width = width > 380 ? width : 380;
    canvas.height += formatedEUserName ? (isLongText ? 80 : 40) : 0;
  }

  if (ens) {
    const ensMaxWidth = maxWidth + 50;
    ensLines = getLines(ens, ensMaxWidth, font500);
    canvas.height += ensLines.length * 30;
  }
  return {
    eText,
    isLongText,
    ensLines
  };
}

async function drawSingPass({ canvas, ctx, eText, isLongText, font, top }: any) {
  ctx.font = font;
  ctx.fillStyle = '#101828';
  ctx.textBaseline = 'middle';
  if (eText) {
    const singpassImage = await generateImg(singpassImg);
    if (isLongText) {
      ctx.textAlign = 'center';
      ctx.drawImage(singpassImage, canvas.width / 2 - 56, top, 132, 26);
      ctx.fillText(':', canvas.width / 2 + 60, top + 10);
      ctx.fillText(eText, canvas.width / 2, top + 50);
      return top + 70;
    } else {
      ctx.drawImage(singpassImage, 30, top, 132, 26);
      ctx.fillText(':', 163, top + 10);
      ctx.fillText(eText, 175, top + 10);
      return top + 30;
    }
  } else {
    return top;
  }
}

async function drawUaePass({ canvas, ctx, eText, isLongText, font, top }: any) {
  ctx.font = font;
  ctx.fillStyle = '#101828';
  ctx.textBaseline = 'middle';
  if (eText) {
    const uaepassImage = await generateImg(uaeImg);
    if (isLongText) {
      ctx.textAlign = 'center';
      ctx.drawImage(uaepassImage, canvas.width / 2 - 56, top, 92, 26);
      ctx.fillText(eText, canvas.width / 2, top + 50);
      return top + 70;
    } else {
      ctx.drawImage(uaepassImage, 50, top, 92, 26);
      ctx.fillText(eText, 155, top + 10);
      return top + 30;
    }
  } else {
    return top;
  }
}

function drawEns({
  ensLines = [],
  canvas,
  ctx,
  font500,
  top
}: {
  ensLines: string[];
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  font500: string;
  top: number;
}) {
  if (ensLines.length > 0) {
    ctx.fillStyle = '#344054';
    ctx.font = font500;
    if (ensLines.length === 1) {
      ctx.textAlign = 'center';
      ctx.fillText(ensLines[0], canvas.width / 2, top + 15);
      return top + 30;
    } else {
      ctx.textAlign = 'left';
      ensLines.forEach((line, index) => {
        ctx.fillText(line, (canvas.width - 330) / 2, top + 15 + 30 * index);
      });
      return top + 30 * ensLines.length;
    }
  } else {
    return top;
  }
}

export async function generateSignPic(img: string, user: ICurrentUser): Promise<any> {
  const font = '400 28px IBM Plex Sans ';
  const font500 = '500 28px IBM Plex Sans ';
  const maxWidth = 250 + 30;
  const scale = 0.6;
  const image = await generateImg(img);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;

  const {
    address,
    singpassUserName,
    singpassUserId,
    ens,
    displayAddress,
    chainId,
    uaepassUserId,
    uaepassUserName,
    eKyc
  } = user || {};
  const isUae = eKyc === 'uaepass';
  const eUserName = isUae ? uaepassUserName : singpassUserName;
  const eUserId = isUae ? uaepassUserId : singpassUserId;
  const isBeraChain = chainId === 80085;
  const shortenedAddress = shortenWalletAddress(displayAddress || address || '', 'shorter');
  canvas.width = 380 + 30;

  let imageWidth = 0;
  let imageHeight = 0;
  if (image.width > image.height) {
    imageWidth = 360 * scale;
    imageHeight = (image.height / image.width) * 360 * scale;
    canvas.height = imageHeight + 120;
  } else {
    imageWidth = (image.width / image.height) * 200 * scale;
    imageHeight = 200 * scale;
    canvas.height = 240;
  }

  const { eText, isLongText, ensLines } = calculateCanvasSize({
    canvas,
    eUserName,
    eUserId,
    font,
    maxWidth,
    font500,
    ens
  });

  ctx.drawImage(image, (canvas.width - imageWidth) / 2, 30, imageWidth, imageHeight);
  const drawEInfo = isUae ? drawUaePass : drawSingPass;
  const eTop = await drawEInfo({ canvas, ctx, isLongText, eText, font, top: imageHeight + 60 });

  const ensTop = drawEns({ ensLines, canvas, ctx, font500, top: eTop });

  const logoImage = await generateImg(isBeraChain ? beraLogoImg : logoImg);
  ctx.drawImage(
    logoImage,
    (canvas.width - (152 + (isBeraChain ? 124 : 178))) / 2,
    ensTop + 6,
    isBeraChain ? 124 : 178,
    39
  );
  ctx.fillStyle = '#D75D00';
  ctx.textAlign = 'left';
  ctx.fillText(
    ' ' + shortenedAddress,
    (canvas.width - (152 + (isBeraChain ? 124 : 178))) / 2 + (isBeraChain ? 124 : 178),
    ensTop + 25
  );

  ctx.beginPath();
  ctx.lineWidth = 8;
  ctx.strokeStyle = '#FAEFE7';
  ctx.strokeRect(0, 0, canvas.width, canvas.height);
  return { data: canvas.toDataURL(), width: canvas.width / 4, height: canvas.height / 4 };
}
export function deletePicBorder(img: string): Promise<any> {
  return new Promise((resolve) => {
    const image = new Image();
    image.src = img;
    image.onload = function () {
      const canvas = document.createElement('canvas');
      canvas.width = image.width - 16;
      canvas.height = image.height - 16;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(image, -8, -8, image.width, image.height);

      resolve(canvas.toDataURL());
    };
  });
}

const typeToAnnotationType = {
  freetext: AnnotationEditorType.FREETEXT,
  stamp: AnnotationEditorType.STAMP
};
export async function convertXmlToJSON(data: string): Promise<any> {
  try {
    if (data.startsWith('<?xml')) {
      const res = await xml2js.parseStringPromise(data);
      const annots: any = [];
      const obj = res?.xfdf?.annots?.[0];
      for (const item in obj) {
        obj[item]?.forEach((element: any, i: number) => {
          const data = element.$;
          annots.push({
            data: data.custom && JSON.parse(data.custom),
            editorId: `pdfjs_${item}_${i}_${getNanoidEn(5)}`,
            pageIndex: Number(data.page),
            rotation: 0,
            rect: data.rect.split(',').map((item: any) => Number(item)),
            annotationType: typeToAnnotationType[item as keyof typeof typeToAnnotationType],
            fontSize: 12,
            color: [0, 0, 0],
            editable: false,
            value: element.contents?.[0] || '',
            noAutoFocus: true,
            bitmapFile: element.imagedata?.[0]
          });
        });
      }
      return JSON.stringify(annots);
    } else {
      return data;
    }
  } catch (error) {
    return data;
  }
}
